import { defineStore } from 'pinia'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import api from '@/services/axiosApi'
import type { Page, CreatePagePayload, UpdatePagePayload } from '../types'

// Helper to extract page data from API response (handles both wrapped and unwrapped responses)
const extractPageData = (res: any): Page => res.data.data ?? res.data

export const usePageStore = defineStore('page', () => {
  const configStore = useConfigStore()
  const { currentTeamPid } = storeToRefs(configStore)
  const queryClient = useQueryClient()

  // Query for fetching all pages
  const pagesQuery = useQuery({
    queryKey: ['pages', currentTeamPid],
    queryFn: async () => {
      const res = await api.get(`/api/${currentTeamPid.value}/pages`)
      // API returns array directly or wrapped in data property
      return (res.data.data ?? res.data) as Page[]
    },
    enabled: computed(() => !!currentTeamPid.value)
  })

  const pages = computed(() => pagesQuery.data.value || [])
  const isLoading = computed(
    () => pagesQuery.isLoading.value || pagesQuery.isPending.value
  )

  // Get page by pid
  const getById = (pid: string): Page | undefined => {
    return pages.value.find((page) => String(page.pid) === pid)
  }

  // Filter pages by status
  const getByStatus = (status: Page['status']): Page[] => {
    return pages.value.filter((page) => page.status === status)
  }

  // Filter pages by type
  const getByType = (type: Page['type']): Page[] => {
    return pages.value.filter((page) => page.type === type)
  }

  // Create page mutation
  const createMutation = useMutation({
    mutationFn: (payload: CreatePagePayload) =>
      api
        .post(`/api/${currentTeamPid.value}/pages`, payload)
        .then(extractPageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', currentTeamPid.value] })
    }
  })

  const create = async (payload: CreatePagePayload): Promise<Page> => {
    return createMutation.mutateAsync(payload)
  }

  // Update page mutation
  const updateMutation = useMutation({
    mutationFn: ({ pid, data }: { pid: string; data: UpdatePagePayload }) =>
      api
        .put(`/api/${currentTeamPid.value}/pages/${pid}`, data)
        .then(extractPageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', currentTeamPid.value] })
    }
  })

  const update = async (pid: string, data: UpdatePagePayload): Promise<Page> => {
    return updateMutation.mutateAsync({ pid, data })
  }

  // Delete page mutation
  const deleteMutation = useMutation({
    mutationFn: (pid: string) =>
      api.delete(`/api/${currentTeamPid.value}/pages/${pid}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', currentTeamPid.value] })
    }
  })

  const destroy = async (pid: string): Promise<void> => {
    await deleteMutation.mutateAsync(pid)
  }

  // Publish page mutation
  const publishMutation = useMutation({
    mutationFn: (pid: string) =>
      api
        .post(`/api/${currentTeamPid.value}/pages/${pid}/publish`)
        .then(extractPageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', currentTeamPid.value] })
    }
  })

  const publish = async (pid: string): Promise<Page> => {
    return publishMutation.mutateAsync(pid)
  }

  // Unpublish page mutation
  const unpublishMutation = useMutation({
    mutationFn: (pid: string) =>
      api
        .post(`/api/${currentTeamPid.value}/pages/${pid}/unpublish`)
        .then(extractPageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', currentTeamPid.value] })
    }
  })

  const unpublish = async (pid: string): Promise<Page> => {
    return unpublishMutation.mutateAsync(pid)
  }

  // Archive page mutation
  const archiveMutation = useMutation({
    mutationFn: (pid: string) =>
      api
        .post(`/api/${currentTeamPid.value}/pages/${pid}/archive`)
        .then(extractPageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', currentTeamPid.value] })
    }
  })

  const archive = async (pid: string): Promise<Page> => {
    return archiveMutation.mutateAsync(pid)
  }

  // Duplicate page mutation
  const duplicateMutation = useMutation({
    mutationFn: ({ pid, name }: { pid: string; name?: string }) =>
      api
        .post(`/api/${currentTeamPid.value}/pages/${pid}/duplicate`, { name })
        .then(extractPageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', currentTeamPid.value] })
    }
  })

  const duplicate = async (pid: string, name?: string): Promise<Page> => {
    return duplicateMutation.mutateAsync({ pid, name })
  }

  // Fetch single page
  const fetchPage = async (pid: string): Promise<Page> => {
    const response = await api.get(`/api/${currentTeamPid.value}/pages/${pid}`)
    return extractPageData(response)
  }

  return {
    // State
    pages,
    isLoading,
    pagesQuery,

    // Getters
    getById,
    getByStatus,
    getByType,

    // Actions
    create,
    update,
    destroy,
    publish,
    unpublish,
    archive,
    duplicate,
    fetchPage,

    // Mutations (for loading states)
    createMutation,
    updateMutation,
    deleteMutation,
    publishMutation,
    unpublishMutation,
    archiveMutation,
    duplicateMutation
  }
})
